import { Menu, MenuButton, MenuItem, MenuItems, Transition } from '@headlessui/react';
import { FC, Fragment } from 'react';
import { FiChevronDown } from 'react-icons/fi';
import { merge } from '../../helpers/Utility';
import { withField } from './withField';

export const COUNTRY_CODES = ['GB', 'IE', 'JP'] as const;
export type CountryCode = (typeof COUNTRY_CODES)[number];

export interface AddressValue {
  line1: string;
  line2?: string;
  city: string;
  postcode: string;
  country: CountryCode | '';
}

interface InjectedProps {
  value: AddressValue;
  onChange: (v: AddressValue) => void;
}

interface AddressInputProps {
  name: string;
  className?: string;
  disabled?: boolean;
}

interface CountryOption {
  code: CountryCode;
  name: string;
}

export const DEFAULT_ADDRESS: AddressValue = {
  line1: '',
  line2: '',
  city: '',
  postcode: '',
  country: 'GB'
};

const countryOptions: CountryOption[] = [
  { code: 'GB', name: 'United Kingdom' },
  { code: 'IE', name: 'Ireland' },
  { code: 'JP', name: 'Japan' }
];

const inputClass =
  'block w-full rounded-md sm:text-sm bg-primary-std border border-primary-std focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 px-2 py-2';

export const AddressInput: FC<AddressInputProps & InjectedProps> = ({
  className,
  disabled,
  name,
  onChange,
  value = DEFAULT_ADDRESS
}) => {
  const set = (field: keyof AddressValue) => (e: React.ChangeEvent<HTMLInputElement>) =>
    onChange({ ...value, [field]: e.target.value });

  const selectedCountry = countryOptions.find(c => c.code === value.country) ?? null;

  return (
    <div className={merge('flex flex-col gap-2', className)}>
      <input
        id={`${name}.line1`}
        name={`${name}.line1`}
        disabled={disabled}
        placeholder="Address line 1"
        value={value.line1}
        onChange={set('line1')}
        className={inputClass}
      />

      <input
        id={`${name}.line2`}
        name={`${name}.line2`}
        disabled={disabled}
        placeholder="Address line 2 (optional)"
        value={value.line2 ?? ''}
        onChange={set('line2')}
        className={inputClass}
      />

      <input
        id={`${name}.city`}
        name={`${name}.city`}
        disabled={disabled}
        placeholder="City"
        value={value.city}
        onChange={set('city')}
        className={inputClass}
      />

      <input
        id={`${name}.postcode`}
        name={`${name}.postcode`}
        disabled={disabled}
        placeholder="Postcode / ZIP"
        value={value.postcode}
        onChange={set('postcode')}
        className={inputClass}
      />

      <Menu as="div" className="relative flex-1">
        {({ open }) => (
          <>
            <MenuButton
              type="button"
              disabled={disabled}
              className={merge(
                inputClass,
                'flex items-center justify-between gap-2 cursor-default',
                disabled ? 'cursor-not-allowed opacity-70' : ''
              )}
            >
              <span className="min-w-0 truncate">
                {selectedCountry ? selectedCountry.name : <span className="text-gray-400">Country</span>}
              </span>

              <FiChevronDown className="text-secondary-std shrink-0" />
            </MenuButton>

            <Transition
              as={Fragment}
              show={open}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems
                anchor="bottom start"
                className="overflow-hidden rounded-md border bg-primary-std shadow-lg focus:outline-none z-[9999] min-w-[180px]"
              >
                {countryOptions.map(option => (
                  <MenuItem key={option.code}>
                    {({ active }) => (
                      <button
                        type="button"
                        className={merge('w-full px-3 py-2 text-left sm:text-sm', active ? 'bg-secondary-std' : '')}
                        onClick={() =>
                          onChange({
                            ...value,
                            country: option.code
                          })
                        }
                      >
                        {option.name}
                      </button>
                    )}
                  </MenuItem>
                ))}
              </MenuItems>
            </Transition>
          </>
        )}
      </Menu>
    </div>
  );
};

export const AddressField = withField<AddressValue, AddressInputProps>(AddressInput);
